import { useRef, ChangeEvent, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import moment from "moment";
import {
  getBase64,
  isFileSupported,
  cloneFile,
  FileTooBigError,
  HeicConversionError,
  MAX_HEIC_SIZE_LABEL,
} from "../../utils/file";
import CameraCapture from "./CameraCapture";

import check from "../../images/check.svg";
import trash from "../../images/trash.svg";
import camera from "../../images/camera.svg";
import sync from "../../images/sync.svg";
import pdf from "../../images/pdf.png";

type ButtonPictureProps = {
  title: string;
  value: string | undefined;
  onChange: (base64: string, formatted: string) => void;
  required?: boolean;
  isLabelOptionnal?: boolean;
  iconUrl?: string;
  cameraOnly?: boolean;
};

interface ImageData {
  base64: string;
  formatted: string;
}

const ACCEPT_ANY =
  "image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.pdf";
const ACCEPT_IMAGES = "image/*";
const NATIVE_CAPTURE = "capture" in document.createElement("input");

const isDisplayableImage = (base64: string) =>
  base64.includes("image/png") ||
  base64.includes("image/jpg") ||
  base64.includes("image/jpeg");

function ButtonPicture({
  title,
  onChange,
  value,
  required,
  isLabelOptionnal = true,
  iconUrl,
  cameraOnly = false,
}: ButtonPictureProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const componentName = cameraOnly ? "ButtonCamera" : "ButtonPicture";
  const useOverlay = cameraOnly && !NATIVE_CAPTURE;

  const [currentImage, setCurrentImage] = useState<ImageData | null>(
    value ? { base64: value, formatted: "" } : null
  );
  const [isConverting, setIsConverting] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentImage) {
      onChange(currentImage.base64, currentImage.formatted);
    } else {
      onChange("", "");
    }
  }, [currentImage]);

  const openPicker = () => {
    ReactGA.event({
      category: "click_picture",
      action: "open_camera",
      value: 0,
    });
    window.umami?.track("file_picker_opened", { component: componentName });
    if (useOverlay) {
      setIsCameraOpen(true);
    } else {
      inputRef.current?.click();
    }
  };

  const processFile = async (file: File) => {
    window.umami?.track("file_picked", {
      component: componentName,
      type: file.type || "unknown",
      size: file.size,
    });

    if (!isFileSupported(file)) {
      window.umami?.track("unsupported_file_format", {
        type: file.type,
        name: file.name,
        component: componentName,
      });
      alert(
        "Format non supporté. Formats acceptés : JPG, PNG, GIF, WebP, HEIC, PDF."
      );
      return;
    }

    const time = file.lastModified ? moment(file.lastModified) : moment();
    const formatted = time.format("DD/MM/YYYY HH:mm:ss");

    setIsConverting(true);
    try {
      const cloned = await cloneFile(file);
      const result = await getBase64(cloned);
      ReactGA.event({
        category: "click_picture",
        action: "close_camera_true",
        value: 1,
      });
      window.umami?.track("file_processed", {
        component: componentName,
        final_type: result.type || "unknown",
      });
      setCurrentImage({ base64: result.base64, formatted });
    } catch (err) {
      ReactGA.event({
        category: "click_picture",
        action: "close_camera_error",
        value: 3,
      });
      if (err instanceof FileTooBigError) {
        window.umami?.track("file_error", {
          component: componentName,
          reason: "too_big",
          size: file.size,
        });
        alert(
          `Le fichier est trop volumineux. Taille maximale autorisée : ${MAX_HEIC_SIZE_LABEL}.`
        );
      } else if (err instanceof HeicConversionError) {
        window.umami?.track("file_error", {
          component: componentName,
          reason: "heic_failed",
        });
        alert(
          "Impossible de convertir cette image HEIC. Veuillez réessayer ou utiliser un autre format."
        );
      } else {
        window.umami?.track("file_error", {
          component: componentName,
          reason: "unknown",
          error: String((err as any)?.message || err).substring(0, 200),
        });
        alert("Une erreur est survenue lors du traitement du fichier.");
      }
    } finally {
      setIsConverting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
      return;
    }
    ReactGA.event({
      category: "click_picture",
      action: "close_camera_false",
      value: 2,
    });
    window.umami?.track("file_picker_cancelled", { component: componentName });
  };

  return (
    <div className="button-picture-container">
      {currentImage ? (
        <>
          <div onClick={() => setCurrentImage(null)}>
            <img className="button-picture-icon" alt="trash" src={trash} />
          </div>
          <div className="button-picture-viewer">
            <img
              height={isDisplayableImage(currentImage.base64) ? "" : "36"}
              src={
                isDisplayableImage(currentImage.base64)
                  ? currentImage.base64
                  : pdf
              }
              alt={title}
            />
          </div>
          <div onClick={openPicker}>
            <img className="button-picture-icon-right" alt="sync" src={sync} />
          </div>
        </>
      ) : null}
      <button
        className="button-picture"
        disabled={isConverting}
        onClick={openPicker}
      >
        {isConverting ? (
          <span>Conversion en cours...</span>
        ) : (
          <>
            {iconUrl ? (
              <img
                className="button-picture-icon-left"
                alt=""
                src={iconUrl}
              />
            ) : null}
            <span>
              {title}{" "}
              {required
                ? "(Obligatoire)"
                : isLabelOptionnal
                ? "(Facultatif)"
                : ""}
            </span>
            <img alt="camera" src={currentImage ? check : camera} />
          </>
        )}
      </button>
      {useOverlay ? (
        isCameraOpen ? (
          <CameraCapture
            onCapture={(file) => {
              setIsCameraOpen(false);
              processFile(file);
            }}
            onClose={() => {
              setIsCameraOpen(false);
              window.umami?.track("file_picker_cancelled", {
                component: componentName,
              });
            }}
          />
        ) : null
      ) : (
        <input
          style={{ position: "absolute", top: -1000, left: -1000 }}
          ref={inputRef}
          type="file"
          accept={cameraOnly ? ACCEPT_IMAGES : ACCEPT_ANY}
          capture={cameraOnly ? "environment" : undefined}
          onChange={onInputChange}
        />
      )}
    </div>
  );
}

export default ButtonPicture;
